import { Box, Grid, InputAdornment } from "@material-ui/core";
import PersonIcon from "@material-ui/icons/Person";
import { Alert } from "@material-ui/lab";
import { useFormikContext } from "formik";
import React, { Fragment, useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import styled from "styled-components";
import useFormatters from "../../../../../../../../components/FormattedFields/useFormatters";
import {
  FormikSelect,
  FormikTextField,
} from "../../../../../../../../components/FormikFields";
import FormikDatePicker from "../../../../../../../../components/FormikFields/FormikDatePicker";
import FormikModalCheckboxes from "../../../../../../../../components/FormikFields/FormikModalCheckboxes";
import SimpleAutocomplete from "../../../../../../../../components/FormikFields/SimpleAutocomplete";
import useServiceHolderUniqueCifQuery from "../../../../../../../../queries/serviceHolder/useServiceHolderUniqueCifQuery";
import { mobilityOptionsList } from "../../../../../../../../util/defaults/mobility";
import { sleepQualityOptionList } from "../../../../../../../../util/defaults/sleepQuality";
import { toiletLevelsList } from "../../../../../../../../util/defaults/toiletLevel";
import useDebounce from "../../../../../../../../util/useDebounce";
import FormBox from "../../../FormComponents/FormBox";
import FormBoxTitle from "../../../FormComponents/FormBoxTitle";
import FormSection from "../../../FormComponents/FormSection";
import FormSectionTitle from "../../../FormComponents/FormSectionTitle";
import { TServiceValuationForm } from "../../types";

const StyledAlert = styled(Alert)`
  font-size: 0.875rem;
  line-height: 1.35;
  margin: 0.25rem 0;
`;

interface IServiceHoldersFormProps {
  pathologyOptions: { value: string; label: string }[];
  emotionalStatusOptions: { value: string; label: string }[];
  careAdaptationOptions: { value: string; label: string }[];
  speechOptions: { value: string; label: string }[];
  mobilityOptions: { value: string; label: string }[];
  sleepQualityOptions: { value: string; label: string }[];
  toiletLevelOptions: { value: string; label: string }[];
  serviceHolderUserStatusOptions: { value: string; label: string }[];
  dependencyOptions: { value: string; label: string }[];
}

const ServiceHoldersForm = (props: IServiceHoldersFormProps) => {
  const {
    pathologyOptions,
    emotionalStatusOptions,
    careAdaptationOptions,
    mobilityOptions,
    sleepQualityOptions,
    toiletLevelOptions,
    dependencyOptions,
  } = props;
  const formikContext = useFormikContext<TServiceValuationForm>();

  const { formatMessage } = useIntl();
  const {
    formatMobilityOption,
    formatServiceHolderSleepQuality,
    formatServiceHolderToiletLevel,
  } = useFormatters();
  const debouncedCif1 = useDebounce(formikContext.values.DNI_U1, 500);
  const serviceHolder1UniqueCifQuery =
    useServiceHolderUniqueCifQuery(debouncedCif1);
  const debouncedCif2 = useDebounce(formikContext.values.DNI_U2, 500);
  const serviceHolder2UniqueCifQuery =
    useServiceHolderUniqueCifQuery(debouncedCif2);

  useEffect(() => {
    const filteredServiceHolders = serviceHolder1UniqueCifQuery.data?.filter(
      (sh) => sh.id !== formikContext.values.service_holder_id
    );
    if (filteredServiceHolders && filteredServiceHolders.length > 0) {
      const serviceHolder = filteredServiceHolders[0];
      if (
        !formikContext.values.DNI_U1_used_by ||
        formikContext.values.DNI_U1_used_by.id !== serviceHolder.id
      ) {
        formikContext.setFieldValue("DNI_U1_used_by", {
          id: serviceHolder.id,
          crm_id: serviceHolder.zohoId,
          crm_id_name: serviceHolder.Name,
        });
      }
    } else {
      if (formikContext.values.DNI_U1_used_by) {
        formikContext.setFieldValue("DNI_U1_used_by", null);
      }
    }
  }, [serviceHolder1UniqueCifQuery, formikContext]);

  useEffect(() => {
    const filteredServiceHolders = serviceHolder2UniqueCifQuery.data?.filter(
      (sh) => sh.id !== formikContext.values.service_holder_id
    );
    if (filteredServiceHolders && filteredServiceHolders.length > 0) {
      const serviceHolder = filteredServiceHolders[0];
      if (
        !formikContext.values.DNI_U2_used_by ||
        formikContext.values.DNI_U2_used_by.id !== serviceHolder.id
      ) {
        formikContext.setFieldValue("DNI_U2_used_by", {
          id: serviceHolder.id,
          crm_id: serviceHolder.zohoId,
          crm_id_name: serviceHolder.Name,
        });
      }
    } else {
      if (formikContext.values.DNI_U2_used_by) {
        formikContext.setFieldValue("DNI_U2_used_by", null);
      }
    }
  }, [serviceHolder2UniqueCifQuery, formikContext]);

  const handleUser1AdaptationBlur = () => {
    formikContext.setFieldTouched("Adaptaci_n_de_cuidados_U1", true);
  };

  const handleUser1AdaptationSelect = (value: string | null) => {
    formikContext.setFieldValue("Adaptaci_n_de_cuidados_U1", value);
  };

  const handleUser1AdaptationChange = (value: string | null) => {
    formikContext.setFieldValue("Adaptaci_n_de_cuidados_U1", value);
  };

  const handleUser2AdaptationBlur = () => {
    formikContext.setFieldTouched("Adaptaci_n_de_cuidados_U2", true);
  };

  const handleUser2AdaptationSelect = (value: string | null) => {
    formikContext.setFieldValue("Adaptaci_n_de_cuidados_U2", value);
  };

  const handleUser2AdaptationChange = (value: string | null) => {
    formikContext.setFieldValue("Adaptaci_n_de_cuidados_U2", value);
  };

  return (
    <Fragment>
      {(formikContext.values.Usuarios_a_cuidar === "1" ||
        formikContext.values.Usuarios_a_cuidar === "2") && (
        <FormBox>
          <FormBoxTitle
            title={formatMessage({
              id: "ServiceValuationForm.GeneralDataU1",
              defaultMessage: "Datos Usuario U1",
            })}
            icon={<PersonIcon />}
          />
          <FormSection>
            <Grid container spacing={8}>
              {formikContext.values.DNI_U1_used_by && (
                <Grid item xs={12} md={12}>
                  <StyledAlert severity="error">
                    <FormattedMessage
                      id="ServiceValuationForm.ServiceHolderCifAlreadyInUse"
                      defaultMessage="El DNI que has introducido ya está en uso. Por favor, revísalo o usa el usuario ya existente"
                    />
                  </StyledAlert>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Nombre_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Name"
                      defaultMessage="Nombre"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Primer_apellido_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Surname"
                      defaultMessage="Primer Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Segundo_apellido_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.SecondSurname"
                      defaultMessage="Segundo Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikDatePicker
                  name="Fecha_de_nacimiento_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Birthdate"
                      defaultMessage="Fecha de nacimiento"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="DNI_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.DNI"
                      defaultMessage="DNI"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikSelect
                  name="Grado_Ley_de_dependencia_U1"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.DependencyDegree"
                      defaultMessage="Grado Ley de dependencia"
                    />
                  }
                  options={dependencyOptions}
                  fullWidth
                  required
                  variant="compact"
                  size="small"
                  margin="nomargin"
                  placeholder={formatMessage({
                    id: "Select...",
                    defaultMessage: "Selecciona...",
                  })}
                />
              </Grid>
            </Grid>
          </FormSection>
          {(formikContext.values.Usuarios_a_cuidar === "1" ||
            formikContext.values.Usuarios_a_cuidar === "2") && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.PhysicalStatus",
                  defaultMessage: "Estado físico y cognitivo",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikModalCheckboxes
                    name="Patologias_U1"
                    label={
                      <FormattedMessage
                        id="ExperiencesTable.Pathologies"
                        defaultMessage="Patologías"
                      />
                    }
                    required
                    fullWidth
                    options={pathologyOptions}
                    messages={{
                      addLabel: formatMessage({
                        id: "ServiceValuationForm.AddPathology",
                        defaultMessage: "Añadir",
                      }),
                      dialogCancelButton: formatMessage({
                        id: "ServiceValutaionForm.PathologiesCancel",
                        defaultMessage: "Cancelar",
                      }),
                      dialogAcceptButton: formatMessage({
                        id: "ServiceValuationForm.PathologiesAccept",
                        defaultMessage: "Aceptar",
                      }),
                      dialogTitle: formatMessage({
                        id: "ServiceValuationForm.PathologiesModify",
                        defaultMessage: "Modificar Patologias",
                      }),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_patologias_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.PathologiesComments"
                        defaultMessage="Comentarios patologías"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Medicacion_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Medication"
                        defaultMessage="Medicación"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Dieta"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Diet"
                        defaultMessage="Dieta"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
              <Box mt={10}></Box>
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Habla_expresion_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.SpeechLevel"
                        defaultMessage="Habla / Expresión"
                      />
                    }
                    fullWidth
                    margin="nomargin"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormikTextField
                    name="Peso_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Weight"
                        defaultMessage="Peso"
                      />
                    }
                    fullWidth
                    margin="nomargin"
                    endAdornment={
                      <InputAdornment position="end">kg</InputAdornment>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormikTextField
                    name="Altura_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Height"
                        defaultMessage="Altura"
                      />
                    }
                    fullWidth
                    margin="nomargin"
                    endAdornment={
                      <InputAdornment position="end">cm</InputAdornment>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Movilidad_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Mobility"
                        defaultMessage="Movilidad"
                      />
                    }
                    required
                    options={mobilityOptions}
                    fullWidth
                    isMulti
                    variant="compact"
                    size="small"
                    margin="nomargin"
                    closeMenuOnSelect={false}
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_movilidad_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Mobility_comments"
                        defaultMessage="Comentarios movilidad"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
          {(formikContext.values.Usuarios_a_cuidar === "1" ||
            formikContext.values.Usuarios_a_cuidar === "2") && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.PersonalHygiene",
                  defaultMessage: "Higiene personal y calidad del sueño",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Aseo_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.ToiletLevel"
                        defaultMessage="Aseo"
                      />
                    }
                    options={toiletLevelOptions}
                    fullWidth
                    isMulti
                    closeMenuOnSelect={false}
                    variant="compact"
                    size="small"
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Calidad_del_sue_o_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.SleepQuality"
                        defaultMessage="Calidad del sueño"
                      />
                    }
                    options={sleepQualityOptions}
                    fullWidth
                    variant="compact"
                    size="small"
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Indicaciones_del_aseo_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.ToiletIndications"
                        defaultMessage="Indicaciones de aseo"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Indicaciones_del_sueno_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.SleepIndications"
                        defaultMessage="Indicaciones del sueño"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
          {(formikContext.values.Usuarios_a_cuidar === "1" ||
            formikContext.values.Usuarios_a_cuidar === "2") && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.EmotionalStatus",
                  defaultMessage: "Estado emocional y carácter",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikModalCheckboxes
                    name="Estado_emocional_y_caracter_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.EmotionalStatus"
                        defaultMessage="Estado emocional y carácter"
                      />
                    }
                    fullWidth
                    options={emotionalStatusOptions}
                    messages={{
                      addLabel: formatMessage({
                        id: "ServiceValuationForm.AddEmotionalStatus",
                        defaultMessage: "Añadir",
                      }),
                      dialogCancelButton: formatMessage({
                        id: "ServiceValutaionForm.EmotionalCancel",
                        defaultMessage: "Cancelar",
                      }),
                      dialogAcceptButton: formatMessage({
                        id: "ServiceValuationForm.EmotionalAccept",
                        defaultMessage: "Aceptar",
                      }),
                      dialogTitle: formatMessage({
                        id: "ServiceValuationForm.EmotionalModify",
                        defaultMessage: "Modificar Estado",
                      }),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SimpleAutocomplete
                    onSelect={handleUser1AdaptationSelect}
                    onInputChange={handleUser1AdaptationChange}
                    onBlur={handleUser1AdaptationBlur}
                    hasError={!!formikContext.errors.Adaptaci_n_de_cuidados_U1}
                    touched={formikContext.touched.Adaptaci_n_de_cuidados_U1}
                    errorMessage={
                      formikContext.errors.Adaptaci_n_de_cuidados_U1
                    }
                    inputValue={formikContext.values.Adaptaci_n_de_cuidados_U1}
                    value={formikContext.values.Adaptaci_n_de_cuidados_U1}
                    name="Adaptaci_n_de_cuidados_U1"
                    options={careAdaptationOptions.map(
                      (option) => option.label
                    )}
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.CaretakerAdoption"
                        defaultMessage="Adaptación a cuidados"
                      />
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_estado_emocional_y_caracter_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.EmotionalStatusComments"
                        defaultMessage="Comentarios estado emocional y carácter"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Actividades_y_aficiones_U1"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Activities"
                        defaultMessage="Actividades y aficiones"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
        </FormBox>
      )}
      {formikContext.values.Usuarios_a_cuidar === "2" && (
        <FormBox>
          <FormBoxTitle
            title={formatMessage({
              id: "ServiceValuationForm.GeneralDataU2",
              defaultMessage: "Datos Usuario U2",
            })}
            icon={<PersonIcon />}
          />
          <FormSection>
            <Grid container spacing={8}>
              {formikContext.values.DNI_U2_used_by && (
                <Grid item xs={12} md={12}>
                  <StyledAlert severity="error">
                    <FormattedMessage
                      id="ServiceValuationForm.ServiceHolderCifAlreadyInUse"
                      defaultMessage="El DNI que has introducido ya está en uso. Por favor, revísalo o usa el usuario ya existente"
                    />
                  </StyledAlert>
                </Grid>
              )}
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Nombre_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Name"
                      defaultMessage="Nombre"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Primer_apellido_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Surname"
                      defaultMessage="Primer Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="Segundo_apellido_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.SecondSurname"
                      defaultMessage="Segundo Apellido"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikDatePicker
                  name="Fecha_de_nacimiento_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.Birthdate"
                      defaultMessage="Fecha de nacimiento"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                  size="small"
                  required
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikTextField
                  name="DNI_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.DNI"
                      defaultMessage="DNI"
                    />
                  }
                  fullWidth
                  margin="nomargin"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormikSelect
                  name="Grado_Ley_de_dependencia_U2"
                  label={
                    <FormattedMessage
                      id="ServiceValuationForm.DependencyDegree"
                      defaultMessage="Grado Ley de dependencia"
                    />
                  }
                  required
                  options={dependencyOptions}
                  fullWidth
                  variant="compact"
                  size="small"
                  margin="nomargin"
                  placeholder={formatMessage({
                    id: "Select...",
                    defaultMessage: "Selecciona...",
                  })}
                />
              </Grid>
            </Grid>
          </FormSection>
          {formikContext.values.Usuarios_a_cuidar === "2" && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.PhysicalStatus",
                  defaultMessage: "Estado físico y cognitivo",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikModalCheckboxes
                    name="Patologias_U2"
                    label={
                      <FormattedMessage
                        id="ExperiencesTable.Pathologies"
                        defaultMessage="Patologías"
                      />
                    }
                    fullWidth
                    options={pathologyOptions}
                    messages={{
                      addLabel: formatMessage({
                        id: "ServiceValuationForm.AddPathology",
                        defaultMessage: "Añadir",
                      }),
                      dialogCancelButton: formatMessage({
                        id: "ServiceValutaionForm.PathologiesCancel",
                        defaultMessage: "Cancelar",
                      }),
                      dialogAcceptButton: formatMessage({
                        id: "ServiceValuationForm.PathologiesAccept",
                        defaultMessage: "Aceptar",
                      }),
                      dialogTitle: formatMessage({
                        id: "ServiceValuationForm.PathologiesModify",
                        defaultMessage: "Modificar Patologias",
                      }),
                    }}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_patologias_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.PathologiesComments"
                        defaultMessage="Comentarios patologías"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Medicacion_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Medication"
                        defaultMessage="Medicación"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Dieta_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Diet"
                        defaultMessage="Dieta"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
              <Box mt={10}></Box>
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Habla_expresion_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.SpeechLevel"
                        defaultMessage="Habla / Expresión"
                      />
                    }
                    fullWidth
                    margin="nomargin"
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormikTextField
                    name="Peso_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Weight"
                        defaultMessage="Peso"
                      />
                    }
                    fullWidth
                    margin="nomargin"
                    endAdornment={
                      <InputAdornment position="end">kg</InputAdornment>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormikTextField
                    name="Altura_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Height"
                        defaultMessage="Altura"
                      />
                    }
                    fullWidth
                    margin="nomargin"
                    endAdornment={
                      <InputAdornment position="end">cm</InputAdornment>
                    }
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Movilidad_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Mobility"
                        defaultMessage="Movilidad"
                      />
                    }
                    required
                    options={mobilityOptionsList().map((option) => ({
                      value: option.value,
                      label: formatMobilityOption(option.value),
                    }))}
                    fullWidth
                    isMulti
                    variant="compact"
                    size="small"
                    margin="nomargin"
                    closeMenuOnSelect={false}
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_movilidad_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Mobility_comments"
                        defaultMessage="Comentarios movilidad"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
          {formikContext.values.Usuarios_a_cuidar === "2" && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.EmotionalStatus",
                  defaultMessage: "Estado emocional y carácter",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikModalCheckboxes
                    name="Estado_emocional_y_caracter_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.EmotionalStatus"
                        defaultMessage="Estado emocional y carácter"
                      />
                    }
                    fullWidth
                    options={emotionalStatusOptions}
                    messages={{
                      addLabel: formatMessage({
                        id: "ServiceValuationForm.AddEmotionalStatus",
                        defaultMessage: "Añadir",
                      }),
                      dialogCancelButton: formatMessage({
                        id: "ServiceValutaionForm.EmotionalCancel",
                        defaultMessage: "Cancelar",
                      }),
                      dialogAcceptButton: formatMessage({
                        id: "ServiceValuationForm.EmotionalAccept",
                        defaultMessage: "Aceptar",
                      }),
                      dialogTitle: formatMessage({
                        id: "ServiceValuationForm.EmotionalModify",
                        defaultMessage: "Modificar Estado",
                      }),
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <SimpleAutocomplete
                    onSelect={handleUser2AdaptationSelect}
                    onInputChange={handleUser2AdaptationChange}
                    onBlur={handleUser2AdaptationBlur}
                    hasError={!!formikContext.errors.Adaptaci_n_de_cuidados_U2}
                    touched={formikContext.touched.Adaptaci_n_de_cuidados_U2}
                    errorMessage={
                      formikContext.errors.Adaptaci_n_de_cuidados_U2
                    }
                    inputValue={formikContext.values.Adaptaci_n_de_cuidados_U2}
                    value={formikContext.values.Adaptaci_n_de_cuidados_U2}
                    name="Adaptaci_n_de_cuidados_U2"
                    options={careAdaptationOptions.map(
                      (option) => option.label
                    )}
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.CaretakerAdoption"
                        defaultMessage="Adaptación a cuidados"
                      />
                    }
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Comentarios_estado_emocional_y_caracter_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.EmotionalStatusComments"
                        defaultMessage="Comentarios estado emocional y carácter"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Actividades_y_aficiones_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.Activities"
                        defaultMessage="Actividades y aficiones"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
          {formikContext.values.Usuarios_a_cuidar === "2" && (
            <FormSection>
              <FormSectionTitle
                title={formatMessage({
                  id: "ServiceValuationForm.PersonalHygiene",
                  defaultMessage: "Higiene personal y calidad del sueño",
                })}
              />
              <Grid container spacing={8}>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Calidad_del_sue_o_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.SleepQuality"
                        defaultMessage="Calidad del sueño"
                      />
                    }
                    options={sleepQualityOptionList().map((option) => ({
                      value: option.value,
                      label: formatServiceHolderSleepQuality(option.value),
                    }))}
                    fullWidth
                    variant="compact"
                    size="small"
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikSelect
                    name="Aseo_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.ToiletLevel"
                        defaultMessage="Aseo"
                      />
                    }
                    options={toiletLevelsList().map((option) => ({
                      value: option.value,
                      label: formatServiceHolderToiletLevel(option.value),
                    }))}
                    fullWidth
                    isMulti
                    closeMenuOnSelect={false}
                    variant="compact"
                    size="small"
                    placeholder={formatMessage({
                      id: "Select...",
                      defaultMessage: "Selecciona...",
                    })}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Indicaciones_del_sueno_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.SleepIndications"
                        defaultMessage="Indicaciones del sueño"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormikTextField
                    name="Indicaciones_del_aseo_U2"
                    label={
                      <FormattedMessage
                        id="ServiceValuationForm.ToiletIndications"
                        defaultMessage="Indicaciones de aseo"
                      />
                    }
                    fullWidth
                    multiline
                    minRows="3"
                    maxRows="5"
                    margin="nomargin"
                  />
                </Grid>
              </Grid>
            </FormSection>
          )}
        </FormBox>
      )}
    </Fragment>
  );
};

export default ServiceHoldersForm;
